<template>
  <main class="py-4 sm:py-8 sm:p-8">
    <!-- Header -->
    <div class="mb-10 flex items-center justify-between">
      <div>
        <PageTitle>Gerencie seus usuários</PageTitle>
        <PageDescription>
          Tenha acesso a todas as pessoas que entraram em contato com o seu bot.
        </PageDescription>
      </div>
      <Button @click="openCreateSubscriptionDialog()">
        <Plus class="h-4 w-4 mr-2" />
        Nova assinatura
      </Button>
    </div>

    <!-- Filters -->
    <div class="mb-6 flex flex-wrap items-end gap-3">
      <div class="flex flex-col gap-1.5">
        <Label class="text-xs text-zinc-400 uppercase tracking-wider">Buscar</Label>
        <div class="relative">
          <Search class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 pointer-events-none" />
          <Input
            v-model="filterSearch"
            placeholder="Nome, usuário..."
            class="pl-9 w-[220px]"
          />
        </div>
      </div>

      <div class="flex flex-col gap-1.5">
        <Label class="text-xs text-zinc-400 uppercase tracking-wider">Grupo</Label>
        <Select v-model="filterGroupId" @update:model-value="onFilterChange">
          <SelectTrigger class="w-[200px]">
            <SelectValue placeholder="Todos os grupos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Todos os grupos</SelectItem>
            <SelectItem v-for="group in groups?.data" :key="group.id" :value="String(group.id)">
              {{ group.name }}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div class="flex flex-col gap-1.5">
        <Label class="text-xs text-zinc-400 uppercase tracking-wider">Plano de pagamento</Label>
        <Select v-model="filterPaymentPlanId" @update:model-value="onFilterChange">
          <SelectTrigger class="w-[220px]">
            <SelectValue placeholder="Todos os planos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Todos os planos</SelectItem>
            <SelectItem v-for="plan in paymentPlans?.data" :key="plan.id" :value="String(plan.id)">
              {{ plan.name }}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div class="flex flex-col gap-1.5">
        <Label class="text-xs text-zinc-400 uppercase tracking-wider">Assinatura</Label>
        <Select v-model="filterSubscriptionStatus" @update:model-value="onFilterChange">
          <SelectTrigger class="w-[200px]">
            <SelectValue placeholder="Todos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="active">Assinantes ativos</SelectItem>
            <SelectItem value="inactive">Assinantes inativos</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div class="flex flex-col gap-1.5">
        <Label class="text-xs text-zinc-400 uppercase tracking-wider">Período</Label>
        <Popover>
          <PopoverTrigger as-child>
            <Button variant="outline" class="w-[200px] justify-start text-left font-normal">
              <CalendarIcon class="h-4 w-4 mr-2" />
              <span v-if="hasActiveDateFilters" class="truncate">Filtros de data ativos</span>
              <span v-else class="text-zinc-400">Filtrar por período</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent class="w-[min(420px,calc(100vw-2rem))] space-y-4">
            <div class="space-y-1.5">
              <Label class="text-xs text-zinc-400 uppercase tracking-wider">Data de entrada</Label>
              <div class="flex items-center gap-2">
                <Input v-model="filterJoinedAtFrom" type="date" class="min-w-0 flex-1" />
                <span class="shrink-0 text-zinc-500 text-xs">até</span>
                <Input v-model="filterJoinedAtTo" type="date" class="min-w-0 flex-1" />
              </div>
            </div>
            <div class="space-y-1.5">
              <Label class="text-xs text-zinc-400 uppercase tracking-wider">Último pagamento</Label>
              <div class="flex items-center gap-2">
                <Input v-model="filterLastPaymentAtFrom" type="date" class="min-w-0 flex-1" />
                <span class="shrink-0 text-zinc-500 text-xs">até</span>
                <Input v-model="filterLastPaymentAtTo" type="date" class="min-w-0 flex-1" />
              </div>
            </div>
            <div class="space-y-1.5">
              <Label class="text-xs text-zinc-400 uppercase tracking-wider">Saída (expiração)</Label>
              <div class="flex items-center gap-2">
                <Input v-model="filterSubscriptionExpiresAtFrom" type="date" class="min-w-0 flex-1" />
                <span class="shrink-0 text-zinc-500 text-xs">até</span>
                <Input v-model="filterSubscriptionExpiresAtTo" type="date" class="min-w-0 flex-1" />
              </div>
            </div>
            <div class="flex justify-between pt-1">
              <Button variant="ghost" size="sm" class="text-zinc-400 hover:text-white" @click="clearDateFilters">
                Limpar datas
              </Button>
              <Button size="sm" @click="onFilterChange">Aplicar</Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <Button
        v-if="filterSearch || filterGroupId !== '0' || filterPaymentPlanId !== '0' || filterSubscriptionStatus !== 'all' || hasActiveDateFilters"
        variant="ghost"
        size="sm"
        class="text-zinc-400 hover:text-white"
        @click="clearFilters"
      >
        <X class="h-4 w-4 mr-1" />
        Limpar filtros
      </Button>
    </div>

    <!-- Leads Table Card -->
    <TableContainer>
      <div class="mb-6 flex items-center justify-between">
        <h2 class="text-xl font-semibold text-white">Usuários registrados</h2>
        <div class="text-sm text-zinc-400">Total: {{ leads?.pagination?.total }}</div>
      </div>

      <!-- Table -->
      <Table class="hidden md:block">
        <TableHeader>
          <TableRow>
            <TableHead v-for="header in tableHeaders" :key="header.key" :class="header.class">
              <div class="flex items-center gap-2">
                {{ header.label }}
                <ArrowsUpDownIcon
                  v-if="header.sortable"
                  class="h-4 w-4 cursor-pointer text-gray-500 hover:text-white"
                />
              </div>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <template v-if="leads">
            <TableFallback
              v-if="leads.data.length === 0"
              :colspan="tableHeaders.length"
              :icon="Contact"
              title="Nenhum lead encontrado"
              description="Aguarde e volte novamente mais tarde"
            />
            <TableRow v-for="lead in leads.data" :key="lead.id">
              <TableCell>
                <div class="text-sm font-medium text-white">{{ lead.firstName }}</div>
              </TableCell>
              <TableCell>
                <div class="text-sm font-medium text-white">{{ lead.lastName }}</div>
              </TableCell>
              <TableCell>
                <div class="text-sm font-medium text-white">{{ lead.username }}</div>
              </TableCell>
              <TableCell>
                <div class="text-sm font-medium text-white">{{ lead.isBuyer ? 'Sim' : 'Não' }}</div>
              </TableCell>
              <TableCell class="text-sm">
                <LeadStatusBadge
                  :status="lead.isSubscriber ? lead.isActiveSubscriber ? 'activeSubscriber' : 'inactiveSubscriber' : 'nonSubscriber'"
                />
              </TableCell>
              <TableCell class="whitespace-nowrap">
                <div class="text-sm text-zinc-300">{{ formatDate(lead.joinedAt) }}</div>
              </TableCell>
              <TableCell class="whitespace-nowrap">
                <div class="text-sm text-zinc-300">{{ formatDate(lead.lastPaymentAt) }}</div>
              </TableCell>
              <TableCell class="whitespace-nowrap">
                <div class="text-sm" :class="lead.subscriptionExpiresAt && !lead.isActiveSubscriber ? 'text-red-400' : 'text-zinc-300'">{{ formatDate(lead.subscriptionExpiresAt) }}</div>
              </TableCell>
              <TableCell>
                <div class="flex items-center gap-1">
                  <Button variant="ghost" size="icon" title="Ver detalhes" @click="showLead(lead.id)">
                    <Eye class="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" title="Nova assinatura" @click="openCreateSubscriptionDialog(lead.id)">
                    <PlusCircle class="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" title="Ir para chat" @click="goToChat(lead)">
                    <MessageCircle class="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          </template>
          <TableRowSkeleton v-else :columns="tableHeaders.length" />
        </TableBody>
      </Table>

      <!-- Mobile List -->
      <div class="space-y-4 md:hidden text-left" v-if="leads">
        <template v-if="leads.data.length > 0">
          <MobileCard v-for="lead in leads.data" :key="lead.id">
            <template #header>
              <div class="flex flex-col">
                <span class="text-[10px] uppercase font-bold text-zinc-500 tracking-wider">Lead</span>
                <span class="text-white font-bold text-lg">{{ lead.firstName }} {{ lead.lastName }}</span>
              </div>
            </template>
            <template #actions>
              <div class="flex items-center gap-1">
                <button @click="goToChat(lead)" class="text-zinc-400 hover:text-white p-1">
                  <MessageCircle class="size-5" />
                </button>
                <button @click="openCreateSubscriptionDialog(lead.id)" class="text-zinc-400 hover:text-white p-1">
                  <PlusCircle class="size-5" />
                </button>
                <button @click="showLead(lead.id)" class="text-zinc-400 hover:text-white p-1">
                  <Eye class="size-5" />
                </button>
              </div>
            </template>
            <template #content>
              <MobileDataLabel label="Usuário">{{ lead.username }}</MobileDataLabel>
              <div class="flex items-center justify-between pt-2">
                <div class="flex flex-col">
                  <span class="text-[10px] uppercase font-bold text-zinc-500 tracking-wider mb-1">Comprador</span>
                  <div class="text-sm font-medium text-white">{{ lead.isBuyer ? 'Sim' : 'Não' }}</div>
                </div>
                <div class="flex flex-col items-end">
                  <span class="text-[10px] uppercase font-bold text-zinc-500 tracking-wider mb-1">Assinatura</span>
                  <LeadStatusBadge
                    :status="lead.isSubscriber ? lead.isActiveSubscriber ? 'activeSubscriber' : 'inactiveSubscriber' : 'nonSubscriber'"
                  />
                </div>
              </div>
              <div class="grid grid-cols-2 gap-2 pt-2">
                <MobileDataLabel label="Data de entrada">{{ formatDate(lead.joinedAt) }}</MobileDataLabel>
                <MobileDataLabel label="Último pagamento">{{ formatDate(lead.lastPaymentAt) }}</MobileDataLabel>
                <MobileDataLabel label="Saída">{{ formatDate(lead.subscriptionExpiresAt) }}</MobileDataLabel>
              </div>
            </template>
          </MobileCard>
        </template>
        <div v-else class="text-center py-12 text-zinc-500">
          <Contact class="size-8 mx-auto mb-3 text-zinc-700" />
          Nenhum lead encontrado
        </div>
      </div>

      <!-- Pagination -->
      <Pagination
        @update:page="page => reloadAndGetAll({ page, perPage, groupId: activeGroupId, paymentPlanId: activePaymentPlanId })"
        v-if="leads?.pagination"
        :data="leads?.pagination"
        v-model:per-page="perPage"
      />
    </TableContainer>

    <!-- Lead Detail Dialog -->
    <Dialog v-model:open="isShowLeadDialogOpen">
      <DialogScrollContent class="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Usuário: {{ selectedLead?.firstName }} {{ selectedLead?.lastName }}</DialogTitle>
          <DialogDescription>Monitore as informações do seu usuário.</DialogDescription>
        </DialogHeader>
        <div v-if="selectedLead" class="py-4">
          <div class="flex items-center justify-between mb-4">
            <h4 class="text-xl font-semibold text-white">Assinaturas de grupos</h4>
            <div class="flex items-center gap-2">
              <Button size="sm" variant="ghost" class="text-white" title="Ir para chat" @click="goToChat(selectedLead)">
                <MessageCircle class="h-4 w-4 mr-1" />
                Chat
              </Button>
              <Button size="sm" variant="outline" class="text-white" @click="openCreateSubscriptionDialog(selectedLead.id)">
                <Plus class="h-4 w-4 mr-1" />
                Adicionar
              </Button>
            </div>
          </div>
          <div class="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead v-for="header in groupSubscriptionTableHeaders" :key="header.key">
                    {{ header.label }}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableFallback
                  v-if="selectedLead.groupSubscriptions.length === 0"
                  :colspan="groupSubscriptionTableHeaders.length"
                  :icon="CreditCard"
                  title="Nenhuma assinatura encontrada"
                  description="Clique em Adicionar para criar uma."
                />
                <TableRow v-for="sub in selectedLead.groupSubscriptions" :key="sub.id">
                  <TableCell class="max-w-[160px]">
                    <div class="text-sm font-medium text-white truncate">{{ sub.group?.name }}</div>
                  </TableCell>
                  <TableCell class="whitespace-nowrap">
                    <span v-if="sub.expiresAt" class="text-zinc-300">{{ new Date(sub.expiresAt).toLocaleString('pt-BR') }}</span>
                    <span v-else class="text-zinc-400">Vitalício</span>
                  </TableCell>
                  <TableCell>
                    <StatusBadge :status="sub.hasAccess ? 'active' : 'inactive'" />
                  </TableCell>
                  <TableCell>
                    <div class="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        class="text-white"
                        :disabled="renewingSubscriptionId === sub.id || revokingSubscriptionId === sub.id"
                        @click="renewSubscription(sub.id)"
                        title="Renovar assinatura"
                      >
                        <Loader2 v-if="renewingSubscriptionId === sub.id" class="h-4 w-4 animate-spin" />
                        <RefreshCw v-else class="h-4 w-4" />
                        <span class="ml-1 hidden sm:inline">Renovar</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        :disabled="!sub.hasAccess || revokingSubscriptionId === sub.id || renewingSubscriptionId === sub.id"
                        class="text-red-400 hover:text-red-300 hover:bg-red-950/40 disabled:opacity-40"
                        @click="revokeTargetSubId = sub.id"
                        title="Revogar assinatura"
                      >
                        <Loader2 v-if="revokingSubscriptionId === sub.id" class="h-4 w-4 animate-spin" />
                        <ShieldOff v-else class="h-4 w-4" />
                        <span class="ml-1 hidden sm:inline">Revogar</span>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>
        <Loader2 v-else class="w-10 h-10 animate-spin text-white mx-auto" />
      </DialogScrollContent>
    </Dialog>

    <!-- Create Manual Subscription Dialog -->
    <Dialog v-model:open="isCreateSubscriptionDialogOpen">
      <DialogScrollContent class="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Nova assinatura de grupo</DialogTitle>
          <DialogDescription>
            Crie assinaturas para um ou mais leads em um ou mais grupos.
          </DialogDescription>
        </DialogHeader>

        <form @submit.prevent="submitCreateSubscription" class="space-y-5 py-4">

          <!-- Leads (multi-select) -->
          <div class="space-y-1.5">
            <Label>Leads <span class="text-red-400">*</span></Label>
            <TagsInput class="px-0 gap-0 w-full" :model-value="selectedLeadNames">
              <div class="flex gap-2 flex-wrap items-center px-3">
                <TagsInputItem v-for="(name, i) in selectedLeadNames" :key="i" :value="name">
                  <TagsInputItemText />
                  <TagsInputItemDelete @click="removeSelectedLead(i)" />
                </TagsInputItem>
              </div>
              <ComboboxRoot
                v-model="selectedLeadNames"
                v-model:open="isLeadComboboxOpen"
                v-model:search-term="searchLead"
                class="w-full"
              >
                <ComboboxAnchor as-child>
                  <ComboboxInput placeholder="Buscar lead..." as-child>
                    <TagsInputInput
                      @focus="isLeadComboboxOpen = true"
                      class="w-full px-3"
                      :class="selectedLeadIds.length > 0 ? 'mt-2' : ''"
                      @keydown.enter.prevent
                    />
                  </ComboboxInput>
                </ComboboxAnchor>
                <ComboboxPortal>
                  <ComboboxContent>
                    <CommandList
                      position="popper"
                      class="w-[--radix-popper-anchor-width] rounded-md mt-2 border bg-popover text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 z-50"
                    >
                      <div v-if="isSearchingLeads" class="py-3 text-center text-sm text-zinc-400">
                        <Loader2 class="h-4 w-4 animate-spin mx-auto" />
                      </div>
                      <template v-else>
                        <CommandEmpty v-if="searchLead.trim()">Nenhum lead encontrado</CommandEmpty>
                        <div v-else class="py-3 text-center text-sm text-zinc-500">
                          Digite para buscar leads...
                        </div>
                        <CommandGroup v-if="filteredLeadsForCombobox.length > 0">
                          <CommandItem
                            v-for="lead in filteredLeadsForCombobox"
                            :key="lead.id"
                            :value="leadLabel(lead)"
                            @select.prevent="(ev) => {
                              if (typeof ev.detail.value === 'string') {
                                searchLead = ''
                                const l = filteredLeadsForCombobox.find(x => leadLabel(x) === ev.detail.value)
                                if (l) { selectedLeadIds.push(l.id); selectedLeadNames.push(leadLabel(l)) }
                              }
                            }"
                          >
                            {{ leadLabel(lead) }}
                          </CommandItem>
                        </CommandGroup>
                      </template>
                    </CommandList>
                  </ComboboxContent>
                </ComboboxPortal>
              </ComboboxRoot>
            </TagsInput>
            <p class="text-xs text-zinc-500">Você pode selecionar mais de 1 lead</p>
          </div>

          <!-- Groups (multi-select) -->
          <div class="space-y-1.5">
            <Label>Grupos <span class="text-red-400">*</span></Label>
            <TagsInput class="px-0 gap-0 w-full" :model-value="selectedGroupNames">
              <div class="flex gap-2 flex-wrap items-center px-3">
                <TagsInputItem v-for="(name, i) in selectedGroupNames" :key="i" :value="name">
                  <TagsInputItemText />
                  <TagsInputItemDelete @click="removeSelectedGroup(i)" />
                </TagsInputItem>
              </div>
              <ComboboxRoot
                v-model="selectedGroupNames"
                v-model:open="isGroupComboboxOpen"
                v-model:search-term="searchGroup"
                class="w-full"
              >
                <ComboboxAnchor as-child>
                  <ComboboxInput placeholder="Buscar grupo..." as-child>
                    <TagsInputInput
                      @focus="isGroupComboboxOpen = true"
                      class="w-full px-3"
                      :class="selectedGroupIds.length > 0 ? 'mt-2' : ''"
                      @keydown.enter.prevent
                    />
                  </ComboboxInput>
                </ComboboxAnchor>
                <ComboboxPortal>
                  <ComboboxContent>
                    <CommandList
                      position="popper"
                      class="w-[--radix-popper-anchor-width] rounded-md mt-2 border bg-popover text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 z-50"
                    >
                      <CommandEmpty>Nenhum grupo encontrado</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          v-for="group in filteredGroupsForCombobox"
                          :key="group.id"
                          :value="group.name"
                          @select.prevent="(ev) => {
                            if (typeof ev.detail.value === 'string') {
                              searchGroup = ''
                              const g = filteredGroupsForCombobox.find(x => x.name === ev.detail.value)
                              if (g) { selectedGroupIds.push(g.id); selectedGroupNames.push(g.name) }
                            }
                            if (filteredGroupsForCombobox.length === 0) isGroupComboboxOpen = false
                          }"
                        >
                          {{ group.name }}
                        </CommandItem>
                      </CommandGroup>
                    </CommandList>
                  </ComboboxContent>
                </ComboboxPortal>
              </ComboboxRoot>
            </TagsInput>
            <p class="text-xs text-zinc-500">Você pode selecionar mais de 1 grupo</p>
          </div>

          <!-- Payment Plan (optional) -->
          <div class="space-y-1.5">
            <Label>
              Plano de pagamento
              <span class="text-zinc-500 text-xs ml-1">(opcional)</span>
            </Label>
            <Select v-model="createFormPaymentPlanId">
              <SelectTrigger class="w-full">
                <SelectValue placeholder="Sem plano" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Sem plano</SelectItem>
                <SelectItem v-for="plan in paymentPlans?.data" :key="plan.id" :value="String(plan.id)">
                  {{ plan.name }}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <!-- Expiration -->
          <div class="space-y-1.5">
            <Label>
              Data de expiração
              <span class="text-zinc-500 text-xs ml-1">(vazio = vitalício)</span>
            </Label>
            <Input v-model="createFormExpiresAt" type="datetime-local" />
          </div>

          <!-- Summary -->
          <div v-if="selectedLeadIds.length > 0 && selectedGroupIds.length > 0" class="rounded-lg bg-zinc-800/60 border border-zinc-700 px-4 py-3 text-sm text-zinc-300">
            Serão criadas <span class="text-white font-semibold">{{ selectedLeadIds.length * selectedGroupIds.length }}</span> assinatura(s)
            para <span class="text-white font-semibold">{{ selectedLeadIds.length }}</span> lead(s)
            em <span class="text-white font-semibold">{{ selectedGroupIds.length }}</span> grupo(s).
          </div>

          <!-- Actions -->
          <div class="flex justify-end gap-3 pt-2">
            <Button type="button" variant="ghost" @click="isCreateSubscriptionDialogOpen = false">
              Cancelar
            </Button>
            <Button type="submit" :disabled="isCreatingSubscription || selectedLeadIds.length === 0 || selectedGroupIds.length === 0">
              <Loader2 v-if="isCreatingSubscription" class="h-4 w-4 mr-2 animate-spin" />
              Criar {{ selectedLeadIds.length * selectedGroupIds.length > 1 ? `${selectedLeadIds.length * selectedGroupIds.length} assinaturas` : 'assinatura' }}
            </Button>
          </div>
        </form>
      </DialogScrollContent>
    </Dialog>
    <!-- Revoke Confirmation Dialog -->
    <Dialog :open="!!revokeTargetSubId" @update:open="(v) => { if (!v) revokeTargetSubId = undefined }">
      <DialogScrollContent class="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Revogar assinatura</DialogTitle>
          <DialogDescription>
            O lead será removido do grupo e receberá uma notificação. Esta ação pode ser revertida renovando a assinatura manualmente.
          </DialogDescription>
        </DialogHeader>
        <div class="flex justify-end gap-3 pt-4">
          <Button variant="ghost" @click="revokeTargetSubId = undefined">Cancelar</Button>
          <Button
            variant="destructive"
            :disabled="!!revokingSubscriptionId"
            @click="revokeSubscription"
          >
            <Loader2 v-if="revokingSubscriptionId" class="h-4 w-4 mr-2 animate-spin" />
            <ShieldOff v-else class="h-4 w-4 mr-2" />
            Revogar acesso
          </Button>
        </div>
      </DialogScrollContent>
    </Dialog>
  </main>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue'
import { useRouter } from 'vue-router'
import { ArrowsUpDownIcon } from '@heroicons/vue/24/outline'
import MobileCard from '@/components/ui/mobile-card/MobileCard.vue'
import MobileDataLabel from '@/components/ui/mobile-card/MobileDataLabel.vue'
import Table from '@/components/ui/table/Table.vue'
import TableHeader from '@/components/ui/table/TableHeader.vue'
import TableBody from '@/components/ui/table/TableBody.vue'
import TableRow from '@/components/ui/table/TableRow.vue'
import TableHead from '@/components/ui/table/TableHead.vue'
import TableFallback from '@/components/ui/table/TableFallback.vue'
import TableCell from '@/components/ui/table/TableCell.vue'
import Pagination from '@/components/ui/pagination/Pagination.vue'
import TableContainer from '@/components/ui/table/TableContainer.vue'
import { Pagination as IPagination } from '@/interfaces/Pagination'
import { Lead as ILead } from '@/interfaces/Lead'
import TableRowSkeleton from '@/components/ui/table/TableRowSkeleton.vue'
import { useToast } from '@/components/ui/toast'
import { Calendar as CalendarIcon, Contact, CreditCard, Eye, Loader2, MessageCircle, Plus, PlusCircle, RefreshCw, Search, ShieldOff, X } from 'lucide-vue-next'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import PageTitle from '@/components/ui/page/PageTitle.vue'
import PageDescription from '@/components/ui/page/PageDescription.vue'
import { GetAllLeadsParams, useLead } from '@/composables/useLeads'
import LeadStatusBadge from '@/components/ui/badge/LeadStatusBadge.vue'
import { Button } from '@/components/ui/button'
import { Dialog, DialogScrollContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import StatusBadge from '@/components/ui/badge/StatusBadge.vue'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useGroup } from '@/composables/useGroup'
import { usePaymentPlan } from '@/composables/usePaymentPlan'
import { useGroupSubscription } from '@/composables/useGroupSubscription'
import { Group } from '@/interfaces/Group'
import { PaymentPlan } from '@/interfaces/PaymentPlan'
import { ComboboxAnchor, ComboboxContent, ComboboxInput, ComboboxPortal, ComboboxRoot } from 'radix-vue'
import { CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command'
import { TagsInput, TagsInputInput, TagsInputItem, TagsInputItemDelete, TagsInputItemText } from '@/components/ui/tags-input'

const router = useRouter()
const { toast } = useToast()

// Table headers
const tableHeaders = [
  { key: 'firstName', label: 'Nome', sortable: false },
  { key: 'lastName', label: 'Sobrenome', sortable: false },
  { key: 'username', label: 'Usuário', sortable: false },
  { key: 'isBuyer', label: 'Comprador' },
  { key: 'groupSubscription', label: 'Assinatura de grupo' },
  { key: 'joinedAt', label: 'Data de entrada' },
  { key: 'lastPaymentAt', label: 'Último pagamento' },
  { key: 'exitAt', label: 'Saída' },
  { key: 'actions', label: '', class: 'w-40' },
]

function formatDate(date?: string | Date) {
  if (!date) return '—'
  return new Date(date).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

const groupSubscriptionTableHeaders = [
  { key: 'group', label: 'Grupo' },
  { key: 'expiresAt', label: 'Expiração' },
  { key: 'hasAccess', label: 'Acesso' },
  { key: 'actions', label: 'Ações' },
]

// Composables
const { getAll: getAllLeads, getById } = useLead()
const { getAll: getAllGroups } = useGroup()
const { getAll: getAllPaymentPlans } = usePaymentPlan()
const { createManual, renew, revoke } = useGroupSubscription()

// Data
const leads = ref<IPagination<ILead>>()
const groups = ref<IPagination<Group>>()
const paymentPlans = ref<IPagination<PaymentPlan>>()

// Pagination
const perPage = ref('10')

// Filters
const filterSearch = ref('')
const filterGroupId = ref('0')
const filterPaymentPlanId = ref('0')
const filterSubscriptionStatus = ref<'all' | 'active' | 'inactive'>('all')
const activeGroupId = ref<number | undefined>()
const activePaymentPlanId = ref<number | undefined>()
const activeSubscriptionStatus = ref<'active' | 'inactive' | undefined>()

// Date range filters (yyyy-mm-dd strings, from <input type="date">)
const filterJoinedAtFrom = ref('')
const filterJoinedAtTo = ref('')
const filterLastPaymentAtFrom = ref('')
const filterLastPaymentAtTo = ref('')
const filterSubscriptionExpiresAtFrom = ref('')
const filterSubscriptionExpiresAtTo = ref('')

const hasActiveDateFilters = computed(() =>
  !!(filterJoinedAtFrom.value || filterJoinedAtTo.value
    || filterLastPaymentAtFrom.value || filterLastPaymentAtTo.value
    || filterSubscriptionExpiresAtFrom.value || filterSubscriptionExpiresAtTo.value)
)

// "até" deve incluir o dia inteiro (23:59:59), senão o backend trunca pra meia-noite
function endOfDay(date: string) {
  return date ? `${date}T23:59:59` : undefined
}

function dateFilterParams() {
  return {
    joinedAtFrom: filterJoinedAtFrom.value || undefined,
    joinedAtTo: endOfDay(filterJoinedAtTo.value),
    lastPaymentAtFrom: filterLastPaymentAtFrom.value || undefined,
    lastPaymentAtTo: endOfDay(filterLastPaymentAtTo.value),
    subscriptionExpiresAtFrom: filterSubscriptionExpiresAtFrom.value || undefined,
    subscriptionExpiresAtTo: endOfDay(filterSubscriptionExpiresAtTo.value),
  }
}

function clearDateFilters() {
  filterJoinedAtFrom.value = ''
  filterJoinedAtTo.value = ''
  filterLastPaymentAtFrom.value = ''
  filterLastPaymentAtTo.value = ''
  filterSubscriptionExpiresAtFrom.value = ''
  filterSubscriptionExpiresAtTo.value = ''
  onFilterChange()
}

let searchTableDebounce: ReturnType<typeof setTimeout>
watch(filterSearch, (term) => {
  clearTimeout(searchTableDebounce)
  searchTableDebounce = setTimeout(() => {
    reloadAndGetAll({
      perPage: perPage.value,
      search: term.trim() || undefined,
      groupId: activeGroupId.value,
      paymentPlanId: activePaymentPlanId.value,
      subscriptionStatus: activeSubscriptionStatus.value,
      ...dateFilterParams(),
    })
  }, 300)
})

// Lead detail dialog
const selectedLead = ref<ILead>()
const isShowLeadDialogOpen = ref(false)
const renewingSubscriptionId = ref<number | undefined>()
const revokingSubscriptionId = ref<number | undefined>()
const revokeTargetSubId = ref<number | undefined>()

// Create subscription dialog
const isCreateSubscriptionDialogOpen = ref(false)
const isCreatingSubscription = ref(false)

// Multi-select: leads
const selectedLeadIds = ref<number[]>([])
const selectedLeadNames = ref<string[]>([])
const searchLead = ref('')
const isLeadComboboxOpen = ref(false)

// Multi-select: groups
const selectedGroupIds = ref<number[]>([])
const selectedGroupNames = ref<string[]>([])
const searchGroup = ref('')
const isGroupComboboxOpen = ref(false)

// Form simple fields
const createFormPaymentPlanId = ref('0')
const createFormExpiresAt = ref('')

// Server-side search for leads combobox
const searchedLeads = ref<ILead[]>([])
const isSearchingLeads = ref(false)
let searchLeadsDebounce: ReturnType<typeof setTimeout>

watch(searchLead, (term) => {
  clearTimeout(searchLeadsDebounce)
  if (!term.trim()) {
    searchedLeads.value = []
    isSearchingLeads.value = false
    return
  }
  isSearchingLeads.value = true
  searchLeadsDebounce = setTimeout(async () => {
    try {
      const result = await getAllLeads({ search: term, perPage: '25' })
      searchedLeads.value = result.data
    } finally {
      isSearchingLeads.value = false
    }
  }, 300)
})

// Computed: filter out already-selected items
const filteredLeadsForCombobox = computed(() =>
  searchedLeads.value.filter(l => !selectedLeadIds.value.includes(l.id))
)
const filteredGroupsForCombobox = computed(() =>
  (groups.value?.data ?? []).filter(g => !selectedGroupIds.value.includes(g.id))
)

const leadLabel = (lead: ILead) =>
  `${lead.firstName}${lead.lastName ? ' ' + lead.lastName : ''}${lead.username ? ' (@' + lead.username + ')' : ''}`

const removeSelectedLead = (index: number) => {
  selectedLeadIds.value.splice(index, 1)
  selectedLeadNames.value.splice(index, 1)
}
const removeSelectedGroup = (index: number) => {
  selectedGroupIds.value.splice(index, 1)
  selectedGroupNames.value.splice(index, 1)
}

// Navigation
const goToChat = (lead: ILead) => {
  router.push({ name: 'chats', query: { search: lead.username || lead.firstName } })
}

const onFilterChange = () => {
  activeGroupId.value = filterGroupId.value !== '0' ? Number(filterGroupId.value) : undefined
  activePaymentPlanId.value = filterPaymentPlanId.value !== '0' ? Number(filterPaymentPlanId.value) : undefined
  activeSubscriptionStatus.value = filterSubscriptionStatus.value !== 'all' ? filterSubscriptionStatus.value : undefined
  reloadAndGetAll({
    perPage: perPage.value,
    search: filterSearch.value.trim() || undefined,
    groupId: activeGroupId.value,
    paymentPlanId: activePaymentPlanId.value,
    subscriptionStatus: activeSubscriptionStatus.value,
    ...dateFilterParams(),
  })
}

const clearFilters = () => {
  filterSearch.value = ''
  filterGroupId.value = '0'
  filterPaymentPlanId.value = '0'
  filterSubscriptionStatus.value = 'all'
  filterJoinedAtFrom.value = ''
  filterJoinedAtTo.value = ''
  filterLastPaymentAtFrom.value = ''
  filterLastPaymentAtTo.value = ''
  filterSubscriptionExpiresAtFrom.value = ''
  filterSubscriptionExpiresAtTo.value = ''
  onFilterChange()
}

const reloadAndGetAll = (params: GetAllLeadsParams) => {
  leads.value = undefined
  getAllLeads(params).then((data) => (leads.value = data))
}

const showLead = async (id: number) => {
  selectedLead.value = undefined
  isShowLeadDialogOpen.value = true
  selectedLead.value = await getById(id)
}

const renewSubscription = async (subscriptionId: number) => {
  renewingSubscriptionId.value = subscriptionId
  try {
    await renew(subscriptionId)
    toast({ title: 'Assinatura renovada com sucesso!' })
    if (selectedLead.value) {
      selectedLead.value = await getById(selectedLead.value.id)
    }
    reloadAndGetAll({ perPage: perPage.value, search: filterSearch.value.trim() || undefined, groupId: activeGroupId.value, paymentPlanId: activePaymentPlanId.value, subscriptionStatus: activeSubscriptionStatus.value, ...dateFilterParams() })
  } catch {
    toast({ title: 'Erro ao renovar assinatura', variant: 'destructive' })
  } finally {
    renewingSubscriptionId.value = undefined
  }
}

const revokeSubscription = async () => {
  if (!revokeTargetSubId.value) return
  const subscriptionId = revokeTargetSubId.value
  revokingSubscriptionId.value = subscriptionId
  revokeTargetSubId.value = undefined
  try {
    await revoke(subscriptionId)
    toast({ title: 'Assinatura revogada com sucesso!' })
    if (selectedLead.value) {
      selectedLead.value = await getById(selectedLead.value.id)
    }
    reloadAndGetAll({ perPage: perPage.value, search: filterSearch.value.trim() || undefined, groupId: activeGroupId.value, paymentPlanId: activePaymentPlanId.value, subscriptionStatus: activeSubscriptionStatus.value, ...dateFilterParams() })
  } catch {
    toast({ title: 'Erro ao revogar assinatura', variant: 'destructive' })
  } finally {
    revokingSubscriptionId.value = undefined
  }
}

const openCreateSubscriptionDialog = (leadId?: number) => {
  selectedLeadIds.value = []
  selectedLeadNames.value = []
  selectedGroupIds.value = []
  selectedGroupNames.value = []
  createFormPaymentPlanId.value = '0'
  createFormExpiresAt.value = ''
  searchLead.value = ''
  searchGroup.value = ''
  searchedLeads.value = []

  if (leadId) {
    const lead = leads.value?.data.find(l => l.id === leadId)
    if (lead) {
      selectedLeadIds.value = [lead.id]
      selectedLeadNames.value = [leadLabel(lead)]
    }
  }

  isCreateSubscriptionDialogOpen.value = true
}

const submitCreateSubscription = async () => {
  if (selectedLeadIds.value.length === 0 || selectedGroupIds.value.length === 0) {
    toast({ title: 'Selecione pelo menos um lead e um grupo', variant: 'destructive' })
    return
  }

  isCreatingSubscription.value = true
  try {
    await createManual({
      leadIds: selectedLeadIds.value,
      groupIds: selectedGroupIds.value,
      paymentPlanId: createFormPaymentPlanId.value !== '0' ? Number(createFormPaymentPlanId.value) : undefined,
      expiresAt: createFormExpiresAt.value || undefined,
    })

    const count = selectedLeadIds.value.length * selectedGroupIds.value.length
    toast({ title: `${count} assinatura(s) criada(s) com sucesso!` })
    isCreateSubscriptionDialogOpen.value = false

    if (isShowLeadDialogOpen.value && selectedLead.value) {
      selectedLead.value = await getById(selectedLead.value.id)
    }
    reloadAndGetAll({ perPage: perPage.value, search: filterSearch.value.trim() || undefined, groupId: activeGroupId.value, paymentPlanId: activePaymentPlanId.value, subscriptionStatus: activeSubscriptionStatus.value, ...dateFilterParams() })
  } catch {
    toast({ title: 'Erro ao criar assinatura(s)', variant: 'destructive' })
  } finally {
    isCreatingSubscription.value = false
  }
}

watch(
  () => perPage.value,
  () => reloadAndGetAll({ perPage: perPage.value, search: filterSearch.value.trim() || undefined, groupId: activeGroupId.value, paymentPlanId: activePaymentPlanId.value, subscriptionStatus: activeSubscriptionStatus.value, ...dateFilterParams() })
)

onMounted(async () => {
  getAllLeads().then((data) => (leads.value = data))
  getAllGroups({ perPage: '100' }).then((data) => (groups.value = data))
  getAllPaymentPlans({ perPage: '100' }).then((data) => (paymentPlans.value = data))
})
</script>
